import { spkiFingerprint, MIN_TENANT_KEY_MODULUS_BITS, type TenantConfig } from '@pink/shared';

import { PinningError } from './pinning-error.js';
import { base64ToBytes } from './base64-to-bytes.js';

/**
 * Erfolg liefert den importierten CryptoKey (extractable=false, usages=['wrapKey']).
 * Fehler liefert den genauen Sub-Code für Telemetrie.
 */
export type PinningResult =
  | { readonly ok: true; readonly verifiedKey: CryptoKey }
  | { readonly ok: false; readonly reason: PinningError };

const IMPORT_ALGORITHM: RsaHashedImportParams = {
  name: 'RSA-OAEP',
  hash: 'SHA-256',
};

/** `extractable: false` und nur `wrapKey` — minimaler Privilegien-Scope. */
const IMPORT_EXTRACTABLE = false;
const IMPORT_USAGES: readonly KeyUsage[] = ['wrapKey'];

interface ImportOutcome {
  readonly key: CryptoKey | null;
  readonly tooWeak: boolean;
}

/**
 * Führt das Public-Key-Pinning aus: Multi-Key-Pool gegen den Pin-Fingerprint
 * (Hex-Lowercase) aus dem Cancel-Token prüfen, Mindest-Modulus erzwingen.
 *
 * Reihenfolge:
 *   1. `tenantPublicKeys` leer → `NO_KEYS_CONFIGURED`.
 *   2. Pro Key: importKey → Modulus-Check → spkiFingerprint → String-Vergleich
 *      gegen `expectedFingerprintHex`. Erster Match gewinnt.
 *   3. Am Ende: `FINGERPRINT_MISMATCH` (wenn irgendein Key importierbar war),
 *      sonst `KEY_TOO_WEAK` (wenn irgendein Key zu schwach war), sonst
 *      `KEY_IMPORT_FAILED`.
 *
 * Der String-Vergleich ist nicht konstant-Zeit — clientseitig im Browser
 * existiert kein Timing-Kanal zum Angreifer.
 *
 * @param config Tenant-Config (Public-Key-Pool).
 * @param expectedFingerprintHex Pin-Fingerprint aus dem dekodierten Token,
 *   Lowercase-Hex, `SPKI_FINGERPRINT_LENGTH` Zeichen.
 */
export async function verifyPinning(
  config: TenantConfig,
  expectedFingerprintHex: string,
): Promise<PinningResult> {
  if (config.tenantPublicKeys.length === 0) {
    return { ok: false, reason: PinningError.NoKeysConfigured };
  }
  return iterateKeys(config.tenantPublicKeys, expectedFingerprintHex.toLowerCase());
}

async function iterateKeys(
  tenantPublicKeys: readonly string[],
  expectedFingerprint: string,
): Promise<PinningResult> {
  let importedAny = false;
  let tooWeakSeen = false;
  for (const keyBase64 of tenantPublicKeys) {
    const outcome = await tryImportAndMatch(keyBase64, expectedFingerprint);
    if (outcome.match !== null) {
      return { ok: true, verifiedKey: outcome.match };
    }
    if (outcome.imported) {
      importedAny = true;
    }
    if (outcome.tooWeak) {
      tooWeakSeen = true;
    }
  }
  return { ok: false, reason: classifyFailure(importedAny, tooWeakSeen) };
}

interface KeyAttempt {
  readonly match: CryptoKey | null;
  readonly imported: boolean;
  readonly tooWeak: boolean;
}

async function tryImportAndMatch(
  keyBase64: string,
  expectedFingerprint: string,
): Promise<KeyAttempt> {
  const attempt = await safeImportKey(keyBase64);
  if (attempt.key === null) {
    return { match: null, imported: false, tooWeak: attempt.tooWeak };
  }
  const computedFingerprint = await safeFingerprint(keyBase64);
  if (computedFingerprint === null) {
    return { match: null, imported: true, tooWeak: false };
  }
  if (computedFingerprint.toLowerCase() === expectedFingerprint) {
    return { match: attempt.key, imported: true, tooWeak: false };
  }
  return { match: null, imported: true, tooWeak: false };
}

async function safeImportKey(keyBase64: string): Promise<ImportOutcome> {
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(keyBase64);
  } catch {
    return { key: null, tooWeak: false };
  }
  try {
    const imported = await crypto.subtle.importKey(
      'spki',
      bytes,
      IMPORT_ALGORITHM,
      IMPORT_EXTRACTABLE,
      [...IMPORT_USAGES],
    );
    if (!hasSufficientModulus(imported)) {
      return { key: null, tooWeak: true };
    }
    return { key: imported, tooWeak: false };
  } catch {
    return { key: null, tooWeak: false };
  }
}

function hasSufficientModulus(key: CryptoKey): boolean {
  const algorithm = key.algorithm as RsaKeyAlgorithm;
  const modulusLength = algorithm.modulusLength;
  return typeof modulusLength === 'number' && modulusLength >= MIN_TENANT_KEY_MODULUS_BITS;
}

async function safeFingerprint(keyBase64: string): Promise<string | null> {
  try {
    return await spkiFingerprint(keyBase64);
  } catch {
    return null;
  }
}

function classifyFailure(importedAny: boolean, tooWeakSeen: boolean): PinningError {
  if (importedAny) {
    return PinningError.FingerprintMismatch;
  }
  if (tooWeakSeen) {
    return PinningError.KeyTooWeak;
  }
  return PinningError.KeyImportFailed;
}
